import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  HSAWithdrawalIcon,
  LinkButton,
  PayAProviderIcon,
  ReimburseMyselfIcon,
  RequiredIcon,
  Text,
} from '@common-fe/common-fe';

import { useMyMoneyHsaWithdrawalFeatureFlag } from '@/modules/UseMyMoney/hooks';
import { UseMyMoneyOption, UseMyMoneyOptionTitle } from '@/modules/UseMyMoney/UseMyMoney.types';

import UseMyMoneyOptionsListItem from './UseMyMoneyOptionsListItem';

interface Props {
  selectedOption?: UseMyMoneyOption;
  setUseMyMoneyOption: (option?: UseMyMoneyOption) => void;
  isReimbursementAllowed?: boolean;
  isBillPayAllowed?: boolean;
}

const UseMyMoneyOptionsList: React.FC<Props> = ({
  selectedOption,
  setUseMyMoneyOption,
  isReimbursementAllowed,
  isBillPayAllowed,
}) => {
  const { t } = useTranslation();
  const { isUseMyMoneyHsaWithdrawalEnabled } = useMyMoneyHsaWithdrawalFeatureFlag();

  const handleClearOption = useCallback(() => {
    setUseMyMoneyOption(undefined);
  }, [setUseMyMoneyOption]);

  const shouldShowOption = (option: UseMyMoneyOption) => {
    return !selectedOption || selectedOption === option;
  };
  
  return (
    <Box data-testid="use-my-money-options-list_container">
      <Box direction="row" align="center" justify="between" height={{ min: '24px' }}>
        <Text data-testid="use-my-money-options-list_question" weight={700}>
          <RequiredIcon size="10px" color="danger" />
          {' '}
          {t('How do you want to use your money?')}
        </Text>
        {selectedOption ? (
          <LinkButton
            testId="use-my-money-options-list_change"
            linkInlineMode
            onClick={handleClearOption}
          >
            {t('Change')}
          </LinkButton>
        ) : null}
      </Box>

      {shouldShowOption(UseMyMoneyOption.REIMBURSE_MYSELF) && isReimbursementAllowed && (
        <UseMyMoneyOptionsListItem
          testId={UseMyMoneyOption.REIMBURSE_MYSELF.toLocaleLowerCase()}
          type={UseMyMoneyOption.REIMBURSE_MYSELF}
          icon={<ReimburseMyselfIcon size="32px" />}
          title={t(UseMyMoneyOptionTitle[UseMyMoneyOption.REIMBURSE_MYSELF])}
          description={t('Already paid for an expense? Add the details to be reimbursed')}
          onChoose={setUseMyMoneyOption}
          isSelected={selectedOption === UseMyMoneyOption.REIMBURSE_MYSELF}
          margin={{ top: 'spacing8' }}
        />
      )}
      {shouldShowOption(UseMyMoneyOption.PAY_A_PROVIDER) && isBillPayAllowed && (
        <UseMyMoneyOptionsListItem
          testId={UseMyMoneyOption.PAY_A_PROVIDER.toLocaleLowerCase()}
          type={UseMyMoneyOption.PAY_A_PROVIDER}
          icon={<PayAProviderIcon size="32px" />}
          title={t(UseMyMoneyOptionTitle[UseMyMoneyOption.PAY_A_PROVIDER])}
          description={t("Have a bill you haven't paid yet? Send your payment directly to the provider.")}
          onChoose={setUseMyMoneyOption}
          isSelected={selectedOption === UseMyMoneyOption.PAY_A_PROVIDER}
          margin={{ top: 'spacing8' }}
        />
      )}
      {shouldShowOption(UseMyMoneyOption.HSA_WITHDRAWAL) && isUseMyMoneyHsaWithdrawalEnabled && (
        <UseMyMoneyOptionsListItem
          testId={UseMyMoneyOption.HSA_WITHDRAWAL.toLocaleLowerCase()}
          type={UseMyMoneyOption.HSA_WITHDRAWAL}
          icon={<HSAWithdrawalIcon size="32px" />}
          title={t(UseMyMoneyOptionTitle[UseMyMoneyOption.HSA_WITHDRAWAL])}
          description={t('Need to pull money from your HSA? Make a quick withdrawal.')}
          onChoose={setUseMyMoneyOption}
          isSelected={selectedOption === UseMyMoneyOption.HSA_WITHDRAWAL}
          margin={{ top: 'spacing8' }}
        />
      )}
    </Box>
  );
};

export default UseMyMoneyOptionsList;
